/**
 * Main container component for the Breeding Workflow
 * Manages state and coordinates all sub-components
 */

import React from 'react';
import { BreedingForm } from '../components/BreedingForm';
import { HybridReview } from '../components/HybridReview';
import { PlantSelector } from '../components/PlantSelector';
import { WorkflowProgress } from '../components/WorkflowProgress';
import { WorkflowSummary } from '../components/WorkflowSummary';
import { useBreedingWorkflow } from '../hooks/useBreedingWorkflow';

export function BreedingWorkflow() {
  const {
    state,
    currentView,
    workflowSteps,
    multiplePlants,
    plantSelectionTitle,
    plantSelectionDescription,
    hybridReview,
    error,
    startWorkflow,
    continueWithPlant,
    confirmHybridWorkflow,
    startOver,
    updateBreedingRunData,
    clearError,
  } = useBreedingWorkflow();

  const hybridRoomName = state.seedRooms.find((room) => room.id === hybridReview?.input.roomId)?.name
    || (hybridReview ? `Room ${hybridReview.input.roomId}` : '');

  return (
    <div className="container">
      <header>
        <h1>🌱 Cultivera Breeding Workflow</h1>
        <p className="subtitle">Selfed Line and F1 Hybrid Seed Creation Tool</p>
      </header>

      <div id="app">
        {/* Main Form */}
        {currentView === 'form' && (
          <BreedingForm
            seedRooms={state.seedRooms}
            defaultRoomId={state.roomId}
            onSubmit={startWorkflow}
          />
        )}

        {/* Plant Selection (shown if multiple matches) */}
        {currentView === 'plantSelection' && (
          <PlantSelector
            plants={multiplePlants}
            title={plantSelectionTitle}
            description={plantSelectionDescription}
            onSelect={continueWithPlant}
          />
        )}

        {/* F1 Hybrid Review */}
        {currentView === 'hybridReview' && hybridReview && (
          <HybridReview
            seedParent={hybridReview.seedParent}
            pollenDonor={hybridReview.pollenDonor}
            generatedName={hybridReview.generatedName}
            finalName={hybridReview.finalName}
            seedCount={hybridReview.input.seedCount}
            roomName={hybridRoomName}
            onConfirm={confirmHybridWorkflow}
            onStartOver={startOver}
          />
        )}

        {/* Progress Display */}
        {currentView === 'progress' && <WorkflowProgress steps={workflowSteps} />}

        {/* Summary */}
        {currentView === 'summary' && (
          <WorkflowSummary
            state={state}
            onStartOver={startOver}
            onUpdateRun={updateBreedingRunData}
          />
        )}

        {/* Error display */}
        {error && (
          <div
            className="error-message"
            style={{ display: 'block' }}
            onClick={clearError}
          >
            L {error}
          </div>
        )}
      </div>

      <footer>
        <p>Walden FarmOps v1.1</p>
      </footer>
    </div>
  );
}
